/**
 * ChatPanel — fullscreen chat overlay with X close button (top-right).
 */

import React, { useEffect, useRef, useState } from "react";
import { View, Text, Pressable, TextInput, ScrollView, Platform } from "react-native";
import { styles } from "./ChatPanel.styles";
import { colors } from "../../styles";
import { useChatStore, ChatMessage } from "../../store/chatStore";
import { useSocketStore } from "../../store/socketStore";

/** Pre-canned story prompts — edit this array to add/remove/change prompts. */
const STORY_PROMPTS = [
  {
    label: "Hoppy's Adventure [short story]",
    text: [
      "Once upon a time, a little bunny named Hoppy found a shiny red apple in the forest.",
      "He called his friend Squirrel Sammy to help carry it home.",
      "Together they shared the apple with Turtle Tommy by the river.",
      "All the friends smiled and said teamwork makes the best adventures!",
    ].join("\n"),
  },
  {
    label: "Grandpa's Mitten [long story]",
    text: [
      "Grandpa was in the winter forest collecting firewood, when he lost his mitten.",
      "The mouse was walking nearby, she was freezing.",
      "The mouse saw the mitten on the ground, it looked cozy so she went inside to warm up.",
      'The fox came by, saw the mouse in the mitten and asked - "little mouse, may I snuggle in the mitten too with you?".',
      '"Of course" said the mouse "come in".',
      "The fox snuggled in the mitten and both were cozy and warm.",
      "Wolf came, they invited him too.",
      "Mama bear came, they invited her too.",
      "All of them were warm and cozy in the mitten and fell asleep.",
      "Grandpa realized he lost his mitten and started looking for it. He found the gang snuggled in his mitten.",
      "Grandpa takes a big stick and slaps the mitten with it.",
      "All the animals jump out and run away.",
      "Grandpa brings firewood back to his house old house where Grandma greets him.",
      "While they sit in front the fireplace he tells her the funny story, both laugh.",
    ].join("\n"),
  },
];

export default function ChatPanel() {
  const { messages, isOpen, currentAiBuffer, focusRequested, setOpen, consumeFocus } = useChatStore();
  const { isVoiceActive, sendTextMessage } = useSocketStore();
  const [input, setInput] = useState("");
  const scrollRef = useRef<ScrollView>(null);
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollToEnd({ animated: false });
  }, [messages, currentAiBuffer]);

  useEffect(() => {
    if (isOpen && focusRequested) {
      requestAnimationFrame(() => inputRef.current?.focus());
      consumeFocus();
    }
  }, [isOpen, focusRequested, consumeFocus]);

  useEffect(() => {
    if (Platform.OS !== "web" || !isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSend = () => {
    const text = input.trim();
    if (!text || !isVoiceActive) return;
    sendTextMessage(text);
    setInput("");
  };

  return (
    <View style={styles.panel}>
      {/* Header with X close */}
      <View style={styles.header}>
        <Text style={styles.title}>Chat</Text>
        <Pressable style={styles.closeBtn} onPress={() => setOpen(false)}>
          <Text style={styles.closeBtnText}>{"\u2715"}</Text>
        </Pressable>
      </View>

      {/* Messages */}
      <ScrollView ref={scrollRef} style={styles.messageList} contentContainerStyle={styles.messageListContent}>
        {messages.map((msg) => (
          <MessageBubble key={msg.id} msg={msg} />
        ))}
        {currentAiBuffer ? (
          <View style={[styles.bubble, styles.aiBubble, { opacity: 0.7 }]}>
            <Text style={styles.bubbleLabel}>AI</Text>
            <Text style={styles.bubbleText}>{currentAiBuffer}</Text>
          </View>
        ) : null}
        {messages.length === 0 && !currentAiBuffer && (
          <View style={styles.emptyArea}>
            <Text style={styles.empty}>
              {isVoiceActive ? "AI responses will appear here..." : "Start a voice session to begin chatting."}
            </Text>
            {isVoiceActive && (
              <View style={styles.promptsArea}>
                <Text style={styles.promptsLabel}>Quick-start a story:</Text>
                {STORY_PROMPTS.map((p) => (
                  <Pressable key={p.label} style={styles.promptBtn} onPress={() => sendTextMessage(p.text)}>
                    <Text style={styles.promptBtnText}>{p.label}</Text>
                  </Pressable>
                ))}
              </View>
            )}
          </View>
        )}
      </ScrollView>

      {/* Input area */}
      <View style={styles.inputArea}>
        <TextInput
          ref={inputRef}
          style={styles.textInput}
          value={input}
          onChangeText={setInput}
          onSubmitEditing={handleSend}
          placeholder={isVoiceActive ? "Type a message..." : "Start voice session first"}
          placeholderTextColor={colors.textMuted}
          editable={isVoiceActive}
          multiline={false}
          returnKeyType="send"
        />
        <Pressable
          style={[styles.sendBtn, { opacity: isVoiceActive && input.trim() ? 1 : 0.4 }]}
          onPress={handleSend}
          disabled={!isVoiceActive || !input.trim()}
        >
          <Text style={styles.sendBtnText}>{"\u27A4"}</Text>
        </Pressable>
      </View>
    </View>
  );
}

function MessageBubble({ msg }: { msg: ChatMessage }) {
  const isUser = msg.role === "user";
  return (
    <View style={[styles.bubble, isUser ? styles.userBubble : styles.aiBubble]}>
      <Text style={styles.bubbleLabel}>
        {isUser ? "You" : "AI"}
        {isUser && msg.source === "text" && <Text style={styles.typedTag}> (typed)</Text>}
      </Text>
      <Text style={styles.bubbleText}>{msg.content}</Text>
    </View>
  );
}
